/**
 * Keeps the screen awake for the length of a voice call (issue #1145).
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A MODULE AND NOT SIX LINES IN THE HOOK
 *
 * `useRealtimeVoice` used to do this inline:
 *
 *     navigator.wakeLock.request("screen")            // once, on dc "open"
 *     document.addEventListener("visibilitychange", …) // the only re-acquire
 *
 * Both halves fail in the Median shell, and they fail quietly:
 *
 * 1. `visibilitychange` NEVER FIRES IN MEDIAN. This repo has now hit that wall
 *    four separate times — CLAUDE.md's "Median native shell" section, issue #16
 *    (TodayTab), PR #627 (EntitlementFlipListener), and this. Median calls a
 *    global `median_app_resumed()` instead. So the one and only re-acquire path
 *    was dead code on device from the day it shipped.
 *
 * 2. A SYSTEM-RELEASED SENTINEL WAS NEVER NOTICED. The platform drops a screen
 *    wake lock on its own — a notification banner, Control Center, a glance at
 *    another app. The spec fires a `release` event on the sentinel when that
 *    happens; nothing listened for it, so the ref kept pointing at a DEAD
 *    sentinel and nothing could tell a held lock from a released one.
 *
 * Together: the first time iOS let go of the lock, it was gone for the rest of
 * the call, and the screen locked mid-conversation.
 *
 * ---------------------------------------------------------------------------
 * TWO MECHANISMS, DELIBERATELY INDEPENDENT
 *
 * `navigator.wakeLock` is a Safari-level API and it is NOT established that the
 * WKWebView shell this app ships in exposes it at all. Median has its own
 * native equivalent, `median.screen.keepScreenOn()`
 * (docs.median.co/docs/keep-screen-on), and it is NOT established that the
 * plugin set in our binary includes it — CLAUDE.md lists our native plugins as
 * OneSignal + Social Login, and the Median docs state no plugin prerequisite
 * either way.
 *
 * So this asks for BOTH and depends on NEITHER. Each is existence-guarded, each
 * records its own outcome, and the call keeps working if both no-op — the screen
 * just locks, which is the bug we already have.
 *
 * NO `median://` URL FALLBACK HERE, on purpose. `useHideKeyboardAccessory` and
 * `useStatusBar` both fall back to `window.location.href = "median://…"` when
 * the JS bridge is missing, and that is right for them. It is NOT right here:
 * Median does not document a URL command for keep-screen-on, and assigning
 * `location.href` to a scheme the shell does not intercept can navigate — which
 * during a live call would tear down the WebRTC peer connection and drop the
 * conversation. A screen that dims is a far better failure than a call that
 * dies. If the JS bridge is absent we record `no-native-api` and move on.
 *
 * ---------------------------------------------------------------------------
 * ON-DEVICE VERIFICATION (Discipline Rule 7)
 *
 * Because neither mechanism is confirmed on our binary, a silent no-op MUST be
 * distinguishable from a real lock without tethering the phone. State lands on
 * `window.__kinVoiceDiag.wakeLock`:
 *
 *   lock         — "held" | "unsupported" | "released" | "released-capped"
 *                  | "hidden" | "idle" | "threw:…"
 *   native       — "on" | "normal" | "no-bridge" | "no-native-api" | "threw:…"
 *   reacquires   — how many times a release event was answered this call
 *   lastTrigger  — what asked for the lock most recently
 *
 * `lock: "held"` + `native: "on"` means both took. `native: "no-native-api"`
 * answers the open plugin question the first time anyone reads it on device.
 * ---------------------------------------------------------------------------
 */

/** Re-acquire attempts allowed in response to *platform* release events before
 *  we stop. A screen that genuinely refuses to stay awake (low power mode) would
 *  otherwise spin request→release→request for the length of the call. Reset to
 *  zero on every deliberate trigger — connect, resume, visibility. */
const DEFAULT_MAX_AUTO_REACQUIRES = 10;

type WakeLockSentinelLike = {
  released?: boolean;
  release: () => Promise<void> | void;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

type MedianScreen = {
  keepScreenOn?: () => void;
  keepScreenNormal?: () => void;
};

export type VoiceWakeDiag = {
  lock: string;
  native: string;
  reacquires: number;
  lastTrigger: string;
};

export type VoiceScreenWake = {
  /** Ask for the screen to stay awake. Safe to call repeatedly. */
  acquire: (trigger?: string) => Promise<void>;
  /** Give the screen back. Safe to call more than once, and from teardown. */
  release: () => void;
  /** Register the resume/visibility re-acquire hooks. Returns an unbind fn. */
  bind: () => () => void;
  /** Test seam — the diagnostics this controller has published so far. */
  readDiag: () => VoiceWakeDiag;
};

/** `window.median` is declared elsewhere (notifications.ts) with a different
 *  member set; re-declaring it with `screen` here would be a conflicting
 *  duplicate declaration, so narrow at the call site — same as
 *  useHideKeyboardAccessory does for `median.keyboard`. */
const getMedian = (): { screen?: MedianScreen } | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { median?: { screen?: MedianScreen } }).median;
};

/** Diagnostics must never throw into a live call. */
const publishDiag = (patch: Partial<VoiceWakeDiag>) => {
  try {
    const w = window as unknown as { __kinVoiceDiag?: { wakeLock?: VoiceWakeDiag } };
    w.__kinVoiceDiag = w.__kinVoiceDiag || {};
    w.__kinVoiceDiag.wakeLock = {
      lock: "idle",
      native: "unknown",
      reacquires: 0,
      lastTrigger: "none",
      ...w.__kinVoiceDiag.wakeLock,
      ...patch,
    };
  } catch {
    /* ignore — a diagnostics failure must never end a call */
  }
};

const readDiag = (): VoiceWakeDiag => {
  const w = window as unknown as { __kinVoiceDiag?: { wakeLock?: VoiceWakeDiag } };
  return (
    w.__kinVoiceDiag?.wakeLock ?? {
      lock: "idle",
      native: "unknown",
      reacquires: 0,
      lastTrigger: "none",
    }
  );
};

/** Median's native keep-awake. Existence-guarded; never throws outward. */
const nativeKeepScreenOn = (): string => {
  const med = getMedian();
  if (!med) return "no-bridge";
  if (typeof med.screen?.keepScreenOn !== "function") return "no-native-api";
  try {
    med.screen.keepScreenOn();
    return "on";
  } catch (e) {
    return `threw:${String(e)}`;
  }
};

const nativeKeepScreenNormal = (): string => {
  const med = getMedian();
  if (!med) return "no-bridge";
  if (typeof med.screen?.keepScreenNormal !== "function") return "no-native-api";
  try {
    med.screen.keepScreenNormal();
    return "normal";
  } catch (e) {
    return `threw:${String(e)}`;
  }
};

export function createVoiceScreenWake(opts: {
  /** The controller re-acquires ONLY while the call is live — a lock that
   *  outlives the call would hold the screen awake on the chat tab forever. */
  isLive: () => boolean;
  maxAutoReacquires?: number;
}): VoiceScreenWake {
  const { isLive, maxAutoReacquires = DEFAULT_MAX_AUTO_REACQUIRES } = opts;

  let sentinel: WakeLockSentinelLike | null = null;
  let releaseListener: (() => void) | null = null;
  let autoReacquires = 0;
  let inFlight = false;

  const detachSentinel = () => {
    if (sentinel && releaseListener) {
      try {
        sentinel.removeEventListener?.("release", releaseListener);
      } catch {
        /* ignore */
      }
    }
    releaseListener = null;
  };

  const acquire = async (trigger = "unknown"): Promise<void> => {
    publishDiag({ lastTrigger: trigger });

    // Native first: synchronous, independent of the standard API, and the only
    // half that works if WKWebView doesn't expose navigator.wakeLock.
    publishDiag({ native: nativeKeepScreenOn() });

    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      publishDiag({ lock: "unsupported" });
      return;
    }
    // Already holding a live one — re-requesting would leak the first sentinel.
    if (sentinel && sentinel.released !== true) {
      publishDiag({ lock: "held" });
      return;
    }
    if (inFlight) return;
    // request() rejects while hidden. Not an error, just not now — the resume
    // hook will ask again when she comes back.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      publishDiag({ lock: "hidden" });
      return;
    }

    inFlight = true;
    try {
      const granted = (await (
        navigator as unknown as {
          wakeLock: { request: (t: string) => Promise<WakeLockSentinelLike> };
        }
      ).wakeLock.request("screen")) as WakeLockSentinelLike;

      detachSentinel();
      sentinel = granted;

      // THE FIX FOR HALF TWO: notice when the platform takes it back.
      releaseListener = () => {
        detachSentinel();
        sentinel = null;
        publishDiag({ lock: "released" });

        if (!isLive()) return;
        if (autoReacquires >= maxAutoReacquires) {
          publishDiag({ lock: "released-capped" });
          return;
        }
        autoReacquires += 1;
        publishDiag({ reacquires: autoReacquires });
        void acquire("release-event");
      };
      granted.addEventListener?.("release", releaseListener);

      publishDiag({ lock: "held" });
    } catch (e) {
      // Refused (low battery, backgrounded, permissions) — never fatal.
      publishDiag({ lock: `threw:${String(e)}` });
    } finally {
      inFlight = false;
    }
  };

  const release = () => {
    // Detach BEFORE releasing: our own release() fires the same `release` event,
    // and the handler would otherwise immediately re-acquire the lock we are
    // deliberately giving up at teardown.
    detachSentinel();
    if (sentinel) {
      try {
        void sentinel.release();
      } catch {
        /* already gone */
      }
      sentinel = null;
    }
    autoReacquires = 0;
    publishDiag({ lock: "idle", native: nativeKeepScreenNormal(), reacquires: 0 });
  };

  const bind = () => {
    const reacquire = (trigger: string) => {
      if (!isLive()) return;
      // A deliberate trigger means the situation changed; give the platform a
      // fresh budget rather than staying capped from earlier in the call.
      autoReacquires = 0;
      void acquire(trigger);
    };

    // Kept for browser/PWA/Playwright, where these DO fire. Dead on device.
    const onVisibility = () => {
      if (document.visibilityState === "visible") reacquire("visibilitychange");
    };
    const onFocus = () => reacquire("focus");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    // ── Median's resume hook — the path that actually runs on device ────────
    // Single global slot, not a listener list: chain whatever was there and
    // restore it on cleanup rather than clobbering another module's handler.
    // Three components already compete for this global.
    const globalWindow = window as unknown as Record<string, unknown>;
    const previousResumeHandler = globalWindow.median_app_resumed;
    globalWindow.median_app_resumed = (...args: unknown[]) => {
      reacquire("median_app_resumed");
      if (typeof previousResumeHandler === "function") {
        try {
          (previousResumeHandler as (...a: unknown[]) => unknown)(...args);
        } catch {
          /* someone else's handler, not ours to fix */
        }
      }
    };

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      globalWindow.median_app_resumed = previousResumeHandler;
    };
  };

  return { acquire, release, bind, readDiag };
}
